import { cn } from '@/lib/utils';
import { WORKFLOW_STEP_META } from '@/config/workflowSteps';
import type { ScriptWorkflowSteps, WorkflowStepKey, WorkflowStepStatus } from '@/types/workflow';

type WorkflowStepperProps = {
  steps: ScriptWorkflowSteps;
  activeStep?: WorkflowStepKey | null;
  analyseSubtext?: string | null;
  onStepClick?: (key: WorkflowStepKey) => void;
  canClickStep?: (key: WorkflowStepKey) => boolean;
};

const STATUS_LABELS: Record<WorkflowStepStatus, string> = {
  complete: 'Complete',
  in_progress: 'In Progress',
  not_started: 'Not Started',
};

function circleClasses(status: WorkflowStepStatus, isActive: boolean, dimmed?: boolean) {
  return cn(
    'relative z-10 flex h-11 w-11 md:h-12 md:w-12 shrink-0 items-center justify-center rounded-full border-2 text-xs md:text-sm font-semibold font-sans transition-all',
    status === 'complete' && 'border-green-500 bg-green-950/80 text-green-400 shadow-[0_0_0_4px_rgba(34,197,94,0.15)]',
    status === 'in_progress' &&
      'border-yellow-400 bg-yellow-950/50 text-yellow-300 shadow-[0_0_0_6px_rgba(250,204,21,0.12)]',
    status === 'not_started' && 'border-[#3a3a3a] bg-[#141414] text-white/40',
    isActive && status === 'in_progress' && 'animate-pulse shadow-[0_0_0_10px_rgba(250,204,21,0.08)]',
    dimmed && 'opacity-50',
  );
}

function statusPillClasses(status: WorkflowStepStatus) {
  return cn(
    'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] uppercase tracking-wider font-sans font-medium',
    status === 'complete' && 'border-green-500/50 text-green-400',
    status === 'in_progress' && 'border-yellow-400/50 text-yellow-300',
    status === 'not_started' && 'border-[#3a3a3a] text-neutral-500',
  );
}

function statusDotClasses(status: WorkflowStepStatus) {
  return cn(
    'h-1.5 w-1.5 rounded-full shrink-0',
    status === 'complete' && 'bg-green-500',
    status === 'in_progress' && 'bg-yellow-400',
    status === 'not_started' && 'bg-neutral-500',
  );
}

function connectorClasses(leftStatus: WorkflowStepStatus) {
  return cn(
    'h-px flex-1 min-w-[12px] self-center',
    leftStatus === 'complete' ? 'bg-green-500/35' : 'bg-[#2a2a2a]',
  );
}

/** Node card: gold outline with interior fill matching the numbered circles. */
function nodeBoxClasses(status: WorkflowStepStatus, isActive: boolean, dimmed?: boolean) {
  return cn(
    'w-full max-w-[148px] min-h-[74px] flex flex-col items-center justify-center gap-2 rounded-lg border-2 px-3 py-3 text-center transition-colors',
    status === 'complete' && 'border-green-500 bg-green-950/80',
    status === 'in_progress' && 'border-index-accent bg-yellow-950/50',
    status === 'not_started' && 'border-index-accent bg-[#141414]',
    isActive && status === 'in_progress' && 'shadow-[0_0_0_4px_rgba(212,175,55,0.15)] animate-pulse',
    dimmed && 'opacity-60 border-gold-button/40',
  );
}

function nodeTitleClasses(status: WorkflowStepStatus) {
  return cn(
    'text-sm md:text-[15px] font-sans font-semibold leading-tight',
    status === 'complete' && 'text-green-400',
    status === 'in_progress' && 'text-yellow-300',
    status === 'not_started' && 'text-white/70',
  );
}

function WorkflowStepper({
  steps,
  activeStep = null,
  analyseSubtext = null,
  onStepClick,
  canClickStep,
}: WorkflowStepperProps) {
  return (
    <div className="w-full">
      <div className="flex w-full gap-2 md:gap-4">
        {WORKFLOW_STEP_META.map((meta, index) => {
          const status = steps[meta.key];
          const isActive = activeStep === meta.key || status === 'in_progress';
          const isLast = index === WORKFLOW_STEP_META.length - 1;
          const clickable = canClickStep?.(meta.key) ?? false;
          const showAnalyseSubtext =
            meta.key === 'analyse' && status === 'in_progress' && analyseSubtext;

          return (
            <div key={meta.key} className="flex min-w-0 flex-1 flex-col items-start">
              <div className="flex w-full items-center">
                <div className={circleClasses(status, isActive, meta.comingSoon)}>{meta.number}</div>
                {!isLast && <div className={connectorClasses(status)} aria-hidden />}
              </div>

              <button
                type="button"
                disabled={!clickable}
                onClick={() => clickable && onStepClick?.(meta.key)}
                className={cn(
                  nodeBoxClasses(status, isActive, meta.comingSoon),
                  'mt-5',
                  clickable &&
                    (status === 'complete'
                      ? 'cursor-pointer hover:border-green-400 hover:bg-green-950'
                      : 'cursor-pointer hover:border-gold-button-dark hover:bg-white/[0.04]'),
                  !clickable && 'cursor-default',
                )}
              >
                <h3 className={nodeTitleClasses(status)}>
                  {meta.title.split(' ').map((word) => (
                    <span key={word} className="block">
                      {word}
                    </span>
                  ))}
                </h3>
                <span className={statusPillClasses(status)}>
                  <span className={statusDotClasses(status)} aria-hidden />
                  {STATUS_LABELS[status]}
                </span>
              </button>

              <div
                className={cn(
                  'mt-2 w-full max-w-[148px] px-0.5 text-center',
                  meta.comingSoon && 'opacity-60',
                )}
              >
                <p className="text-xs text-white/45 font-sans leading-snug">{meta.description}</p>
                {meta.comingSoon && (
                  <p className="mt-1.5 text-[10px] uppercase tracking-wide text-neutral-500 font-sans">
                    Coming Soon
                  </p>
                )}
                {showAnalyseSubtext && (
                  <p className="mt-1.5 text-[11px] text-yellow-300/80 font-sans leading-snug">
                    {analyseSubtext}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default WorkflowStepper;
